/**
 * EUR -> RON for charging Romanian buyers in lei.
 *
 * WHY: Regulamentul BNR nr. 4/2005 art. 3 alin. (1) — payments between Romanian
 * residents for services are made only in lei; no anexa 2 exception covers a domestic
 * SaaS subscription. CMG FRAMEWORKS SRL charging a Romanian SRL in EUR by card breaks
 * it. Foreign buyers (e.g. Smart Tax, BG) stay in EUR: art. 3 governs resident-to-
 * resident operations only.
 *
 * THE LIST STAYS IN EUR. The lei amount is DERIVED (Alex, 2026-10-06):
 *   lei = EUR list price x BNR rate of the prior banking day x 1.02
 * exact to the ban (no rounding step), the theMarketer convention ("1 Eur = <BNR> + 2%",
 * printed on the invoice so a customer checking the BNR rate finds the difference
 * explained).
 *
 * INTEGER MATH ON PURPOSE: 3999 x 5.0912 x 1.02 in floating point can land a hair under
 * a .5 and round the wrong way; a ban off on a fiscal document is a reconciliation diff.
 * The rate is carried in ten-thousandths (BNR publishes 4 decimals) and the markup in
 * basis points, so the only rounding is one explicit half-up division.
 *
 * Pure module: no Stripe client, no network. The rate comes from
 * efactura/bnr-rate.ts getBnrRate (the one hardened BNR client in this repo).
 */

/** +2% over the BNR reference rate (Alex, 2026-10-06; matches theMarketer MKT50097). */
export const RON_FX_MARKUP = 0.02;

/**
 * EUR has traded in a narrow managed band against RON for years; a number outside this
 * is a broken source (zeroed, wrongly based, per-100), never a market move.
 */
export const RON_RATE_MIN = 4.0;
export const RON_RATE_MAX = 7.0;

export class RonFxError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RonFxError';
  }
}

export function assertPlausibleEurRonRate(rate: unknown): asserts rate is number {
  if (typeof rate !== 'number' || !Number.isFinite(rate)) {
    throw new RonFxError(`EUR->RON rate is not a number: ${JSON.stringify(rate)}`);
  }
  if (rate < RON_RATE_MIN || rate > RON_RATE_MAX) {
    throw new RonFxError(
      `EUR->RON rate ${rate} is outside the plausible band ${RON_RATE_MIN}-${RON_RATE_MAX}`
    );
  }
}

function assertMarkup(markup: number): void {
  if (!Number.isFinite(markup) || markup < 0 || markup > 0.2) {
    throw new RonFxError(`implausible FX markup ${markup}`);
  }
}

/**
 * EUR minor units (cents) -> RON minor units (bani): round(eur x rate x (1 + markup)),
 * half-up, in integer arithmetic. 3999 cents at 5.0912 +2% -> 20767 bani (207,67 lei).
 */
export function eurCentsToRonBani(
  eurCents: number,
  rate: number,
  markup: number = RON_FX_MARKUP
): number {
  if (!Number.isInteger(eurCents) || eurCents < 0) {
    throw new RonFxError(`cannot convert EUR amount ${eurCents} (expected whole cents >= 0)`);
  }
  assertPlausibleEurRonRate(rate);
  assertMarkup(markup);
  if (eurCents === 0) return 0;

  const rateE4 = Math.round(rate * 10_000);
  const factorBp = 10_000 + Math.round(markup * 10_000);
  // <= 1e6 cents x 7e4 x 1.2e4 = 8.4e14 < 2^53: exact.
  const numerator = eurCents * rateE4 * factorBp;
  const divisor = 100_000_000;
  const quotient = Math.floor(numerator / divisor);
  const remainder = numerator - quotient * divisor;
  return remainder * 2 >= divisor ? quotient + 1 : quotient;
}

/**
 * Price / invoice-item metadata recording WHICH rate produced a lei amount. The invoice
 * line prints exactly this (never today's rate): what was charged is what is explained.
 */
export const RON_FX_METADATA_KEYS = {
  rate: 'cmg_ron_fx_rate',
  rateDate: 'cmg_ron_fx_rate_date',
  markup: 'cmg_ron_fx_markup',
  eurUnitAmount: 'cmg_ron_eur_unit_amount',
  source: 'cmg_ron_fx_source',
} as const;

export interface RonFxBasis {
  /** EUR minor units the lei amount was converted FROM. */
  eurCents: number;
  /** Lei per one euro, as published by BNR. */
  rate: number;
  /** ISO yyyy-mm-dd of the BNR publication (Cube date) the rate was read from. */
  rateDate: string;
  markup: number;
}

export function ronFxMetadata(basis: RonFxBasis): Record<string, string> {
  return {
    [RON_FX_METADATA_KEYS.rate]: String(basis.rate),
    [RON_FX_METADATA_KEYS.rateDate]: basis.rateDate,
    [RON_FX_METADATA_KEYS.markup]: String(basis.markup),
    [RON_FX_METADATA_KEYS.eurUnitAmount]: String(basis.eurCents),
    [RON_FX_METADATA_KEYS.source]: 'BNR',
  };
}

/** The basis stamped by ronFxMetadata, or null when any key is missing or unusable. */
export function parseRonFxMetadata(
  metadata: Record<string, string> | null | undefined
): RonFxBasis | null {
  if (!metadata) return null;
  const rate = Number(metadata[RON_FX_METADATA_KEYS.rate]);
  const markup = Number(metadata[RON_FX_METADATA_KEYS.markup]);
  const eurCents = Number(metadata[RON_FX_METADATA_KEYS.eurUnitAmount]);
  const rateDate = metadata[RON_FX_METADATA_KEYS.rateDate];
  if (
    metadata[RON_FX_METADATA_KEYS.rate] === undefined ||
    metadata[RON_FX_METADATA_KEYS.markup] === undefined ||
    metadata[RON_FX_METADATA_KEYS.eurUnitAmount] === undefined ||
    !Number.isFinite(rate) ||
    !Number.isFinite(markup) ||
    !Number.isInteger(eurCents) ||
    typeof rateDate !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}$/.test(rateDate)
  ) {
    return null;
  }
  return { eurCents, rate, rateDate, markup };
}

/**
 * True only when `basis` reproduces `ronBani` to the ban. A stale or mismatched basis
 * must never be printed as "the rate that produced this amount".
 */
export function basisProducesAmount(basis: RonFxBasis, ronBani: number): boolean {
  try {
    return eurCentsToRonBani(basis.eurCents, basis.rate, basis.markup) === ronBani;
  } catch {
    return false;
  }
}

/** 39.99 -> "39,99" (Romanian decimal comma, fixed decimals, no grouping). */
export function formatRoDecimal(value: number, decimals: number): string {
  return value.toFixed(decimals).replace('.', ',');
}

/** "2026-10-05" -> "05.10.2026". */
export function formatRoDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

/** 0.02 -> "2%", 0.025 -> "2,5%". */
export function formatMarkupPercent(markup: number): string {
  const pct = Math.round(markup * 10_000) / 100;
  return `${String(pct).replace('.', ',')}%`;
}

/**
 * The basis as printed on a lei invoice line, theMarketer-style:
 * "39,99 EUR × 5,0912 lei/EUR curs BNR 05.10.2026 + 2%".
 */
export function formatRonFxBasisRo(basis: RonFxBasis): string {
  return (
    `${formatRoDecimal(basis.eurCents / 100, 2)} EUR × ` +
    `${formatRoDecimal(basis.rate, 4)} lei/EUR curs BNR ${formatRoDate(basis.rateDate)} + ` +
    formatMarkupPercent(basis.markup)
  );
}
